import Logo from 'components/layout/Images/Logo'
import ClientLandingFooter from 'components/layout/LandingPage/ClientLandingFooter'
import Link from 'components/shared/link'
import { useEffect, useState } from 'react'
import { isHostDomainClient } from 'utils/domainClient'
import translateText from 'app/lib/translatedText'
import WidgetWrapper from 'components/WidgetWrapper'
import { parseTheme } from 'app/lib/parseConfigTheme'
import { cn } from 'app/lib/utils'

interface FooterWidgetProps {
    config: IDomainConfig
    lang: string
    data?: IFooterWidget
}

const FooterWidget = ({ config, lang, data }: FooterWidgetProps) => {
    const [isHost, setIsHost] = useState(false)
    const orgName = config?.site_name || 'DISPORTY'

    // Get layout size from theme
    const theme = config?.theme ? parseTheme(config.theme) : null
    const layoutSize = theme?.branding?.layout_size || 'compact'
    const isWide = layoutSize === 'wide'

    useEffect(() => {
        const isHost = isHostDomainClient()
        setIsHost(isHost)
    }, [])

    const useCustomFooter = data?.useCustomFooter === true

    const showCopyright = data?.showCopyright !== false
    const showLogo = data?.showLogo !== false
    const showOrgName = data?.showOrgName !== false

    const primaryLinks = (data?.primaryLinks || []).slice(0, 3)
    const secondaryLinks = (data?.secondaryLinks || []).slice(0, 5)

    return (
        <>
            {!useCustomFooter ? (
                <>{config && lang && <ClientLandingFooter isHostDomain={isHost} config={config} lang={lang} />}</>
            ) : (
                <WidgetWrapper config={config}>
                    <div
                        className={cn(
                            'w-full justify-between flex flex-col md:flex-row px-4 md:px-0',
                            isWide ? 'mt-16 gap-4 md:gap-0' : 'mt-12 md:mt-16 gap-3 md:gap-0'
                        )}
                    >
                        {(showLogo || showOrgName) && (
                            <div className='flex items-center space-x-4'>
                                {showLogo && <Logo config={config} />}
                                {showOrgName && (
                                    <p
                                        className={cn(
                                            isWide
                                                ? 'text-sm md:text-base extra_large:text-xl'
                                                : 'text-xs md:text-sm extra_large:text-lg'
                                        )}
                                    >
                                        {orgName}
                                    </p>
                                )}
                            </div>
                        )}

                        {primaryLinks.length > 0 && (
                            <div
                                className={cn(
                                    'flex items-center gap-x-6 font-light',
                                    isWide
                                        ? 'text-sm md:text-base extra_large:text-xl'
                                        : 'text-xs md:text-sm extra_large:text-lg'
                                )}
                            >
                                {primaryLinks.map((link, index) => {
                                    const linkText = translateText(lang, link.text || { tr: [] })
                                    if (!linkText || linkText === '<ERROR>') return null
                                    return (
                                        <Link key={index} href={link.link || '#'}>
                                            {linkText}
                                        </Link>
                                    )
                                })}
                            </div>
                        )}
                    </div>

                    <hr
                        className={cn(
                            'border-accent-foreground/50 opacity-50',
                            isWide ? 'md:my-12 my-8' : 'md:my-10 my-6'
                        )}
                    />
                    <div
                        className={cn(
                            'flex items-center justify-between flex-col md:flex-row px-4 md:px-0',
                            isWide ? 'mb-16 gap-4 md:gap-0' : 'mb-12 md:mb-16 gap-3 md:gap-0'
                        )}
                    >
                        <div className='flex flex-col items-center md:items-start gap-4'>
                            {secondaryLinks.length > 0 && (
                                <div
                                    className={cn(
                                        'flex items-center w-fit font-light flex-col md:flex-row',
                                        isWide
                                            ? 'gap-x-5 gap-4 text-sm md:text-base extra_large:text-xl'
                                            : 'gap-x-4 gap-3 text-xs md:text-sm extra_large:text-lg'
                                    )}
                                >
                                    {secondaryLinks.map((link, index) => {
                                        const linkText = translateText(lang, link.text || { tr: [] })
                                        if (!linkText || linkText === '<ERROR>') return null
                                        return (
                                            <Link key={index} href={link.link || '#'}>
                                                {linkText}
                                            </Link>
                                        )
                                    })}
                                </div>
                            )}
                            <nav
                                aria-label='Policies'
                                className='flex flex-wrap justify-center gap-x-4 gap-y-3 text-xs md:text-sm'
                            >
                                <Link href='/info/privacy-policy' className='hover:underline'>
                                    Privacy Policy
                                </Link>
                                <Link href='/info/tos' className='hover:underline'>
                                    Terms of Service
                                </Link>
                                <Link href='/info/cookies-policy' className='hover:underline'>
                                    Cookies Policy
                                </Link>
                            </nav>
                        </div>
                        {showCopyright && (
                            <div
                                className={cn(
                                    isWide
                                        ? 'text-sm md:text-base extra_large:text-xl'
                                        : 'text-xs md:text-sm extra_large:text-lg'
                                )}
                            >
                                Copyright © {config?.site_name || 'DISPORTY'} {new Date().getFullYear()}
                            </div>
                        )}
                    </div>
                </WidgetWrapper>
            )}
        </>
    )
}

export default FooterWidget
